// src/components/blog/BlogContent.jsx
import React, { memo, useEffect, useRef } from 'react';

const BlogContent = memo(({ content, isDark }) => {
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current && content) {
      const headings = contentRef.current.querySelectorAll('h2, h3, h4');
      headings.forEach((heading, index) => {
        if (!heading.id) {
          const text = heading.textContent;
          const id = text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
          heading.id = id || `heading-${index}`;
        }
      });
    }
  }, [content]);

  if (!content) {
    return <div className="text-gray-500 italic">No content available</div>;
  }

  return (
    <div 
      ref={contentRef}
      className={`
        // Base styles
        text-base leading-relaxed
        ${isDark ? 'text-gray-200' : 'text-gray-700'}
        
        // Headings
        [&_h1]:text-4xl [&_h1]:font-extrabold [&_h1]:mb-6 [&_h1]:mt-8
        [&_h1]:text-gray-900 dark:[&_h1]:text-white
        
        [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:pb-3
        [&_h2]:text-gray-800 dark:[&_h2]:text-white
        [&_h2]:border-b-2 [&_h2]:border-blue-700/30
        [&_h2]:relative
        [&_h2]:after:content-[''] [&_h2]:after:absolute [&_h2]:after:bottom-[-2px] [&_h2]:after:left-0
        [&_h2]:after:w-20 [&_h2]:after:h-1 [&_h2]:after:bg-gradient-to-r
        [&_h2]:after:from-blue-700 [&_h2]:after:to-blue-900 [&_h2]:after:rounded-full
        
        [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3
        [&_h3]:text-gray-700 dark:[&_h3]:text-gray-200
        [&_h3]:before:content-['▸'] [&_h3]:before:mr-2 [&_h3]:before:text-blue-700
        
        [&_h4]:text-xl [&_h4]:font-medium [&_h4]:mt-6 [&_h4]:mb-2
        [&_h4]:text-gray-600 dark:[&_h4]:text-gray-300
        
        // Paragraphs - NO drop cap
        [&_p]:mb-5 [&_p]:leading-8
        [&_p]:text-gray-700 dark:[&_p]:text-gray-300
        
        // Links
        [&_a]:text-blue-700 dark:[&_a]:text-blue-400
        [&_a]:font-medium
        [&_a]:underline [&_a]:decoration-blue-700/30 [&_a]:underline-offset-2
        [&_a]:transition-colors [&_a]:duration-200
        hover:[&_a]:decoration-blue-700
        
        // Lists
        [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:space-y-1.5
        [&_ul]:text-gray-700 dark:[&_ul]:text-gray-300
        
        [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-5 [&_ol]:space-y-1.5
        [&_ol]:text-gray-700 dark:[&_ol]:text-gray-300
        
        [&_li]:mb-1.5
        
        // Nested lists
        [&_ul_ul]:list-circle [&_ul_ul]:mt-1 [&_ul_ul]:mb-1
        
        // Blockquotes
        [&_blockquote]:border-l-4 [&_blockquote]:border-l-blue-700
        [&_blockquote]:pl-6 [&_blockquote]:py-2
        [&_blockquote]:my-6
        [&_blockquote]:bg-blue-50/30 dark:[&_blockquote]:bg-blue-900/10
        [&_blockquote]:text-gray-700 dark:[&_blockquote]:text-gray-300
        [&_blockquote]:italic
        [&_blockquote]:rounded-r
        [&_blockquote]:shadow-sm
        
        // Images
        [&_img]:rounded-xl [&_img]:shadow-lg
        [&_img]:my-8
        [&_img]:border [&_img]:border-gray-200 dark:[&_img]:border-gray-700
        [&_img]:transition-all [&_img]:duration-300
        hover:[&_img]:shadow-xl hover:[&_img]:scale-[1.01]
        
        // Tables
        [&_table]:w-full [&_table]:my-6
        [&_table]:shadow-sm [&_table]:rounded-lg [&_table]:overflow-hidden
        [&_table]:border [&_table]:border-gray-200 dark:[&_table]:border-gray-700
        
        [&_th]:bg-gray-100 dark:[&_th]:bg-gray-700
        [&_th]:text-left [&_th]:p-3 [&_th]:font-semibold
        [&_th]:border [&_th]:border-gray-200 dark:[&_th]:border-gray-600
        [&_th]:text-gray-900 dark:[&_th]:text-white
        
        [&_td]:p-3
        [&_td]:border [&_td]:border-gray-200 dark:[&_td]:border-gray-600
        [&_td]:bg-white/50 dark:[&_td]:bg-gray-800/50
        [&_td]:text-gray-700 dark:[&_td]:text-gray-300
        
        [&_tr:even]:bg-gray-50 dark:[&_tr:even]:bg-gray-800/30
        
        // Inline Code
        [&_code]:px-1.5 [&_code]:py-0.5
        [&_code]:rounded
        [&_code]:bg-gray-100 dark:[&_code]:bg-gray-800
        [&_code]:text-blue-700 dark:[&_code]:text-blue-400
        [&_code]:text-sm [&_code]:font-mono [&_code]:font-medium
        
        // Code Blocks
        [&_pre]:bg-gray-900 [&_pre]:text-gray-100
        [&_pre]:p-4 [&_pre]:rounded-lg
        [&_pre]:overflow-x-auto
        [&_pre]:text-sm
        [&_pre]:my-6
        [&_pre]:shadow-lg
        [&_pre]:border [&_pre]:border-gray-700
        
        [&_pre_code]:bg-transparent [&_pre_code]:text-gray-100
        [&_pre_code]:p-0 [&_pre_code]:font-normal
        
        // HR
        [&_hr]:my-12
        [&_hr]:border-0
        [&_hr]:h-px
        [&_hr]:bg-gradient-to-r
        [&_hr]:from-transparent
        [&_hr]:via-gray-300 dark:[&_hr]:via-gray-700
        [&_hr]:to-transparent
        
        // Strong
        [&_strong]:text-gray-900 dark:[&_strong]:text-white
        [&_strong]:font-semibold
        
        // Emphasis
        [&_em]:text-gray-600 dark:[&_em]:text-gray-400
        [&_em]:font-medium
      `}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
});

BlogContent.displayName = 'BlogContent';

export default BlogContent;